import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Button, HStack, Text, Badge, Spinner, Table,
  Thead, Tbody, Tr, Th, Td, TableContainer, Progress
} from '@chakra-ui/react';
import { getBulkJobStatus, downloadBulkJobResult, getOpsJobsByAction } from '../../Http/Index';
import { ShowAlert } from '../Alerts/Alert';
import { BulkAction } from '../../hooks/useBulkOpsJob';

const ACTION_COLOR: Record<string, string> = {
  CREATE: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
  IDENTIFY_ADD: 'teal',
  IDENTIFY_DELETE: 'orange',
};

interface OpsJob {
  jobId: string;
  action: BulkAction;
  status: string;
  total: number;
  completed: number;
  failed: number;
  pending: number;
  percentage: number;
  canDownload: boolean;
  createdAt: string;
  companyName?: string;
  userType?: 'INTERNAL' | 'EXTERNAL';
  uploadedByName?: string;
  uploadedById?: string;
}

interface JobStatus {
  status: string;
  percentage: number;
  total: number;
  completed: number;
  failed: number;
  canDownload: boolean;
}

const BulkJobTracker: React.FC<{ action?: string; refetchTrigger?: number }> = ({ action, refetchTrigger }) => {
  const [jobs, setJobs] = useState<OpsJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobStatuses, setJobStatuses] = useState<Record<string, JobStatus>>({});
  const [checking, setChecking] = useState<Record<string, boolean>>({});
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  const fetchJobs = useCallback(async (pageNo: number) => {
    if (!action) return;
    setLoadingJobs(true);
    try {
      const res = await getOpsJobsByAction(action, pageNo, limit);
      setJobs(res?.data?.data || []);
      setTotalPages(res?.data?.meta?.totalPages ?? 1);
    } catch (e: any) {
      ShowAlert('warning', e?.response?.data?.message || 'Error fetching jobs');
    } finally {
      setLoadingJobs(false);
    }
  }, [action]);

  useEffect(() => {
    setPage(1);
    fetchJobs(1);
  }, [fetchJobs, refetchTrigger]);

  useEffect(() => {
    fetchJobs(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheckStatus = useCallback(async (job: OpsJob) => {
    setChecking((prev) => ({ ...prev, [job.jobId]: true }));
    try {
      const res = await getBulkJobStatus(job.jobId);
      const data = res?.data;
      setJobStatuses((prev) => ({
        ...prev,
        [job.jobId]: {
          status: data?.status || 'PROCESSING',
          percentage: data?.percentage ?? 0,
          total: data?.total ?? 0,
          completed: data?.completed ?? 0,
          failed: data?.failed ?? 0,
          canDownload: data?.canDownload ?? false,
        }
      }));
    } catch (e: any) {
      ShowAlert('warning', e?.response?.data?.message || 'Error fetching job status');
    } finally {
      setChecking((prev) => ({ ...prev, [job.jobId]: false }));
    }
  }, []);

  const handleDownload = useCallback(async (job: OpsJob) => {
    setDownloading((prev) => ({ ...prev, [job.jobId]: true }));
    try {
      const res = await downloadBulkJobResult(job.jobId);
      const url = window.URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      a.download = `${job.action}_${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      ShowAlert('success', 'Result downloaded successfully');
    } catch (e: any) {
      ShowAlert('warning', e?.response?.data?.message || 'Error downloading result');
    } finally {
      setDownloading((prev) => ({ ...prev, [job.jobId]: false }));
    }
  }, []);

  return (
    <Box mt={6} p={4} borderWidth={1} borderRadius="md" borderColor="gray.200" bg="gray.50">
      <HStack justifyContent="space-between" mb={3}>
        <Text fontWeight="bold" fontSize="md" color="gray.700">
          Submitted Jobs — Check & Download Results
        </Text>
        <Button size="xs" colorScheme="gray" onClick={() => fetchJobs(page)} isLoading={loadingJobs}>
          Refresh
        </Button>
      </HStack>

      {loadingJobs && jobs.length === 0 ? (
        <Spinner size="sm" />
      ) : jobs.length === 0 ? (
        <Text fontSize="sm" color="gray.400">No submitted jobs yet. Submit a file above to see job status here.</Text>
      ) : (
        <TableContainer>
          <Table size="sm" variant="simple">
            <Thead bg="gray.100">
              <Tr>
                <Th>Job ID</Th>
                <Th>Company</Th>
                {!['IDENTIFY_ADD', 'IDENTIFY_DELETE'].includes(action || '') && (
                  <Th>Uploaded By</Th>
                )}
                <Th>Action</Th>
                <Th>Created At</Th>
                <Th>Progress</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {jobs.map((job) => {
                const s = jobStatuses[job.jobId];
                const isReady = s?.canDownload || s?.status === 'COMPLETED' || job.canDownload;
                const pct = s?.percentage ?? job.percentage;
                return (
                  <Tr key={job.jobId} bg="white">
                    <Td fontFamily="mono" fontSize="xs" color="gray.600">{job.jobId}</Td>
                    <Td fontSize="xs" color="gray.700" fontWeight="medium">
                      {job.companyName || '—'}
                    </Td>
                    {!['IDENTIFY_ADD', 'IDENTIFY_DELETE'].includes(action || '') && (
                      <Td fontSize="xs">
                        {job.userType === 'EXTERNAL' ? (
                          <Badge colorScheme="orange" fontSize="xs">External User</Badge>
                        ) : (
                          <HStack spacing={1}>
                            <Badge colorScheme="purple" fontSize="xs">Internal</Badge>
                            <Text fontSize="xs" color="gray.600">
                              ({job.uploadedByName || 'Ops Team'})
                            </Text>
                          </HStack>
                        )}
                      </Td>
                    )}
                    <Td>
                      <Badge colorScheme={ACTION_COLOR[job.action] || 'gray'} fontSize="xs">
                        {job.action}
                      </Badge>
                    </Td>
                    <Td fontSize="xs" color="gray.500">
                      {new Date(job.createdAt).toLocaleString('en-GB', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit', hour12: true
                      })}
                    </Td>
                    <Td minW="180px">
                      <Box>
                        <HStack justifyContent="space-between" mb={1}>
                          <Text fontSize="xs" color="gray.500">
                            {(s?.completed ?? job.completed)}/{(s?.total ?? job.total)}
                            {(s?.failed ?? job.failed) > 0 && (
                              <Text as="span" color="red.400"> ({s?.failed ?? job.failed} failed)</Text>
                            )}
                          </Text>
                          <Text fontSize="xs" fontWeight="bold" color={isReady ? 'green.500' : 'gray.600'}>{pct}%</Text>
                        </HStack>
                        <Progress value={pct} size="sm" colorScheme={isReady ? 'green' : 'blue'} borderRadius="full" />
                        <Text fontSize="xs" color="gray.400" mt={1}>{s?.status ?? job.status}</Text>
                      </Box>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button
                          size="xs"
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => handleCheckStatus(job)}
                          isDisabled={checking[job.jobId]}
                          minW="90px"
                        >
                          {checking[job.jobId] ? <Spinner size="xs" /> : 'Check Status'}
                        </Button>
                        <Button
                          size="xs"
                          colorScheme="teal"
                          onClick={() => handleDownload(job)}
                          isDisabled={!isReady || downloading[job.jobId]}
                          isLoading={downloading[job.jobId]}
                        >
                          Download
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {totalPages > 1 && (
        <HStack justifyContent="center" mt={4} spacing={2}>
          <Button size="xs" onClick={() => setPage(p => p - 1)} isDisabled={page === 1 || loadingJobs}>
            Prev
          </Button>
          <Text fontSize="xs" color="gray.600">Page {page} of {totalPages}</Text>
          <Button size="xs" onClick={() => setPage(p => p + 1)} isDisabled={page === totalPages || loadingJobs}>
            Next
          </Button>
        </HStack>
      )}
    </Box>
  );
};

export default BulkJobTracker;
